import axiosInstance from "./axiosInstance";

/**
 * Upload a single file directly to Amazon S3 using Presigned PUT URL
 * (Bypasses server memory/multer for fast, scalable direct production uploads)
 * @param {File|Blob} file - File object to upload
 * @param {string} [folder="uploads"] - Optional folder/category name in S3
 * @returns {Promise<{ url: string, key: string, publicId: string, originalName: string, mimeType: string, size: number }>}
 */
export const s3Upload = async (file, folder = "uploads") => {
  if (!file) throw new Error("File is required for upload");

  try {
    // 1. Get presigned PUT URL from backend
    const presignedRes = await axiosInstance.post("/upload/presigned-put-url", {
      fileName: file.name || "file",
      fileType: file.type || "application/octet-stream",
      folder: folder || "uploads",
    });

    if (!presignedRes?.data?.success || !presignedRes?.data?.data?.uploadUrl) {
      throw new Error(presignedRes?.data?.message || "Failed to get presigned upload URL");
    }

    const { uploadUrl, key, fileUrl } = presignedRes.data.data;

    // 2. Direct PUT upload straight to Amazon S3
    const s3Response = await fetch(uploadUrl, {
      method: "PUT",
      headers: {
        "Content-Type": file.type || "application/octet-stream",
      },
      body: file,
    });

    if (!s3Response.ok) {
      throw new Error(`S3 direct upload failed with status ${s3Response.status}`);
    }

    return {
      url: fileUrl,
      key,
      publicId: key,
      originalName: file.name || "file",
      mimeType: file.type || "application/octet-stream",
      size: file.size || 0,
    };
  } catch (directError) {
    console.warn("Direct S3 upload encountered an issue, trying server fallback:", directError);
    // Graceful fallback to server route if direct upload fails (e.g. before CORS is configured)
    const formData = new FormData();
    formData.append("file", file);
    if (folder) formData.append("folder", folder);

    const fallbackRes = await axiosInstance.post("/upload/s3", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    if (!fallbackRes?.data?.success) {
      throw directError;
    }

    return fallbackRes.data.data;
  }
};

/**
 * Upload multiple files directly to Amazon S3 using Presigned PUT URLs
 * @param {FileList|Array<File>} files - Array or FileList of files
 * @param {string} [folder="uploads"] - Optional folder/category name in S3
 * @returns {Promise<Array<{ url: string, key: string, publicId: string, originalName: string, mimeType: string, size: number }>>}
 */
export const s3UploadMultiple = async (files, folder = "uploads") => {
  if (!files || files.length === 0) throw new Error("Files are required for upload");

  const fileList = Array.from(files);
  const uploadPromises = fileList.map((file) => s3Upload(file, folder));
  return await Promise.all(uploadPromises);
};

/**
 * Delete a file from Amazon S3
 * @param {string} keyOrUrl - S3 Object Key or full URL
 */
export const s3Delete = async (keyOrUrl) => {
  if (!keyOrUrl) return;
  const response = await axiosInstance.delete("/upload/s3", {
    data: { key: keyOrUrl },
  });
  return response.data;
};

export default s3Upload;
